import React, { useEffect, useMemo, useState } from 'react';
import compareDesc from 'date-fns/compareDesc';
import parse from 'date-fns/parse';
import {
  GlobalResponse,
  SingleStrapiImage,
  Slug,
} from '../../../models/domain/interfaces';
import { fromHumanNumberOfColumnsToRepeaterNumberOfColumns } from '../../../utils/carousels';
import { normalizeAttributesNewsAndEventsBanner } from '../../../utils/event';
import { getEvents, getNews } from '../../../utils/api';

import {
  MainTitle,
  RepeaterWrapper,
  StyledContentWrapper,
} from './BannerNewsAndEvents.style';
import { EventCardType } from '../../../models/enum';
import { Media } from '../../../models/auto-generated-strapi-types/types/Media';
import NewsAndEventCardsRepeater from './NewsAndEventCardsRepeater/NewsAndEventCardsRepeater';

export enum ItemType {
  NEWS = 'news',
  EVENT = 'event',
}

interface BaseCardProps {
  url?: any;
  title?: string;
  description?: string;
  category?: string;
  coverImage: { data: Media };
  image?: SingleStrapiImage;
  startDate?: string;
  endDate?: string;
  type?: ItemType;
  fair?: string;
  location?: string;
  stand?: string;
}

interface NewsAndEventsItem {
  id: number;
  attributes: BaseCardProps;
}

interface SliderNewsEventsProps {
  title: string;
  items: NewsAndEventsItem[];
}

const BannerNewsAndEvents: React.FC<any> = ({
  data,
}: {
  data: SliderNewsEventsProps;
}) => {
  const { title, items } = data ?? {};

  const cards = useMemo(
    () =>
      items &&
      Array.isArray(items) &&
      items?.map(({ id, attributes }) => {
        return {
          id: id,
          attributes: {
            category: attributes?.category,
            title: attributes?.title,
            description: attributes?.description,
            url: attributes?.url,
            coverImage: attributes?.coverImage,
            image: attributes?.image,
            startDate: attributes?.startDate,
            endDate: attributes?.endDate,
            fair: attributes?.fair,
            location: attributes?.location,
            type: attributes?.type,
          },
        };
      }),
    [items]
  );

  return (
    <StyledContentWrapper>
      <MainTitle>{title ?? ''}</MainTitle>
      <RepeaterWrapper>
        <NewsAndEventCardsRepeater
          spacing={3}
          cols={{
            md: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(4),
            sm: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(3),
            xs: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(1),
          }}
          props={{
            cardType: EventCardType.SMALL,
          }}
          data={cards && cards?.slice(0, 4)}
        />
      </RepeaterWrapper>
    </StyledContentWrapper>
  );
};

export const getServerSideProps = async (
  section: any,
  translations: Record<string, any>,
  slugAnalyzed: Slug,
  globalLocale: GlobalResponse
) => {
  const news = (
    await getNews({
      locale: slugAnalyzed?.locale,
      pageSize: 4,
      sort: ['date:desc'],
      datesFormat: translations?.date?.sliderNewsEvents?.format,
    })
  ).data;

  const events = await getEvents({
    locale: slugAnalyzed?.locale,
    pageSize: 4,
    sort: ['publishedAt:desc'],
    datesFormat: translations?.date?.sliderNewsEvents?.format,
  });

  const sorted = [
    ...(normalizeAttributesNewsAndEventsBanner(
      news,
      ItemType.NEWS,
      translations,
      globalLocale?.attributes?.newsAndEventsMerged || false,
      slugAnalyzed.locale
    ) || []),
    ...(normalizeAttributesNewsAndEventsBanner(
      events,
      ItemType.EVENT,
      translations,
      globalLocale?.attributes?.newsAndEventsMerged || false,
      slugAnalyzed.locale
    ) || []),
  ].sort((a, b) => {
    const lhs = parse(
      a?.attributes?.date,
      translations?.date?.sliderNewsEvents?.format,
      new Date()
    );
    const rhs = parse(
      b?.attributes?.date,
      translations?.date?.sliderNewsEvents?.format,
      new Date()
    );
    return compareDesc(lhs, rhs);
  });

  section.items = sorted;
};

export default BannerNewsAndEvents;
